import { CloseOutlined } from "@ant-design/icons"
import { Alert, Button, Flex, Form, Modal, Select, Tooltip, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import type { MilestoneBulkResult } from "../../../../../redux/project-milestones/project-milestones.types.ts"
import { renderMilestoneStatusOption } from "./MilestoneStatusTag.tsx"

const { Text } = Typography

export type MilestoneBulkAction = "change-status" | "delete"

export interface MilestoneBulkActionParams {
  action: MilestoneBulkAction
  ids: string[]
  status?: number
}

interface StatusOption {
  label: string
  value: number
}

interface MilestonesBulkActionBarProps {
  selectedIds: string[]
  // A selection survives loading more rows, so a failure can name a milestone
  // whose row is not currently mounted — fall back to the id when it is absent.
  milestoneNames: Record<string, string>
  statusOptions: StatusOption[]
  editAllowed?: boolean
  deleteAllowed?: boolean
  onClear: () => void
  onAction: (params: MilestoneBulkActionParams) => Promise<MilestoneBulkResult>
  /**
   * The bar outlives the selection — it keeps reporting the outcome after an
   * action clears it — so anything anchored to the same corner follows this
   * rather than the selection.
   */
  onVisibleChange?: (visible: boolean) => void
}

type BulkFormValues = { status?: number }

export function MilestonesBulkResult({
  result,
  milestoneNames
}: {
  result: MilestoneBulkResult
  milestoneNames: Record<string, string>
}) {
  const { t } = useTranslation()

  return (
    <div>
      <Text strong>
        {t("milestoneBulk.result.summary", {
          succeeded: result.succeeded.length,
          requested: result.requested
        })}
      </Text>
      {/* Bounded because the bar is pinned to the bottom: an unbounded list
          grows upwards over the table rather than overflowing down. */}
      {result.failed.length ? (
        <ul
          data-testid="milestones-bulk-result-failures"
          // Nothing inside takes focus, so the region carries it — otherwise
          // the refusals below the cap are unreachable without a pointer.
          tabIndex={0}
          aria-label={t("milestoneBulk.result.failuresLabel", {
            count: result.failed.length
          })}
          className="mb-0 mt-2 max-h-[30vh] list-disc overflow-y-auto pl-5 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#2A7D8C]"
        >
          {result.failed.map((failure) => (
            <li key={failure.id}>
              {t("milestoneBulk.result.failure", {
                milestone: milestoneNames[failure.id] || failure.id,
                reason: failure.message
              })}
            </li>
          ))}
        </ul>
      ) : null}
    </div>
  )
}

export function MilestonesBulkActionBar({
  selectedIds,
  milestoneNames,
  statusOptions,
  editAllowed,
  deleteAllowed,
  onClear,
  onAction,
  onVisibleChange
}: MilestonesBulkActionBarProps) {
  const { t } = useTranslation()
  const [form] = Form.useForm<BulkFormValues>()
  const [activeAction, setActiveAction] = useState<MilestoneBulkAction | null>(
    null
  )
  const [submitting, setSubmitting] = useState(false)
  const [result, setResult] = useState<MilestoneBulkResult | null>(null)
  const [requestFailed, setRequestFailed] = useState(false)

  const actionLabels = useMemo<Record<MilestoneBulkAction, string>>(
    () => ({
      "change-status": t("milestoneBulk.actions.changeStatus"),
      delete: t("milestoneBulk.actions.delete")
    }),
    [t]
  )

  const closeModal = () => {
    if (submitting) return
    setActiveAction(null)
    form.resetFields()
  }

  const openModal = (action: MilestoneBulkAction) => {
    form.resetFields()
    setActiveAction(action)
  }

  const submitAction = async () => {
    if (!activeAction) return

    let values: BulkFormValues
    try {
      values = await form.validateFields()
    } catch {
      return
    }

    // Never fan a status change out with nothing to set — the field's own rule
    // covers this, but the request must not depend on the form being connected
    // in time.
    if (activeAction === "change-status" && values.status == null) return

    setSubmitting(true)
    try {
      const nextResult = await onAction({
        action: activeAction,
        ids: selectedIds,
        ...(activeAction === "change-status" ? { status: values.status } : {})
      })
      setRequestFailed(false)
      setResult(nextResult)
      setActiveAction(null)
      form.resetFields()
    } catch {
      setResult(null)
      setRequestFailed(true)
      setActiveAction(null)
      form.resetFields()
    } finally {
      setSubmitting(false)
    }
  }

  const clear = () => {
    setResult(null)
    setRequestFailed(false)
    onClear()
  }

  const visible = !!selectedIds.length || !!result || requestFailed

  useEffect(() => {
    onVisibleChange?.(visible)
  }, [visible, onVisibleChange])

  if (!visible) return null

  const buttonClass =
    "!h-9 !border-[#2A7D8C] !bg-[#2A7D8C] !text-white hover:!border-[#236b78] hover:!bg-[#236b78]"

  return (
    <div
      className="sticky bottom-0 z-30"
      data-testid="milestones-bulk-action-bar"
    >
      {selectedIds.length ? (
        <Flex
          align="center"
          gap="small"
          className="overflow-x-auto bg-[#17383E] px-4 py-3 text-white"
          role="toolbar"
          aria-label={t("milestoneBulk.toolbarLabel")}
          wrap={false}
        >
          <Text strong className="mr-2 whitespace-nowrap !text-white">
            {t("milestoneBulk.selected", { count: selectedIds.length })}
          </Text>

          {editAllowed ? (
            <Button
              className={buttonClass}
              onClick={() => openModal("change-status")}
            >
              {actionLabels["change-status"]}
            </Button>
          ) : null}

          {deleteAllowed ? (
            <Button danger type="primary" onClick={() => openModal("delete")}>
              {actionLabels.delete}
            </Button>
          ) : null}

          <Tooltip title={t("milestoneBulk.clearSelection")}>
            <Button
              // The fixed support launcher expands from the right edge; the
              // trailing margin keeps this dismiss action clickable next to it.
              className="ml-auto mr-16 shrink-0 !text-white"
              type="text"
              icon={<CloseOutlined />}
              aria-label={t("milestoneBulk.clearSelection")}
              onClick={clear}
            />
          </Tooltip>
        </Flex>
      ) : null}

      {/* Below the toolbar, so a result does not push the controls still in
          use further down the page. */}
      {requestFailed ? (
        <Alert
          className="rounded-t-none"
          data-testid="milestones-bulk-request-error"
          type="error"
          showIcon
          closable
          onClose={() => setRequestFailed(false)}
          message={t("milestoneBulk.result.requestFailed")}
        />
      ) : null}

      {result ? (
        <Alert
          className="rounded-t-none"
          type={result.failed.length ? "warning" : "success"}
          showIcon
          closable
          onClose={() => setResult(null)}
          message={
            <MilestonesBulkResult
              result={result}
              milestoneNames={milestoneNames}
            />
          }
        />
      ) : null}

      <Modal
        open={activeAction != null}
        title={activeAction ? actionLabels[activeAction] : undefined}
        okText={
          activeAction === "delete"
            ? t("milestoneBulk.actions.delete")
            : t("milestoneBulk.apply")
        }
        cancelText={t("milestoneBulk.cancel")}
        okButtonProps={{ danger: activeAction === "delete" }}
        confirmLoading={submitting}
        cancelButtonProps={{ disabled: submitting }}
        closable={!submitting}
        keyboard={!submitting}
        maskClosable={false}
        destroyOnHidden
        onOk={submitAction}
        onCancel={closeModal}
      >
        <Form form={form} layout="vertical" disabled={submitting}>
          {activeAction === "change-status" ? (
            <Form.Item<BulkFormValues>
              name="status"
              label={t("milestoneBulk.fields.status")}
              rules={[
                {
                  required: true,
                  message: t("milestoneBulk.validation.status")
                }
              ]}
            >
              <Select
                options={statusOptions}
                placeholder={t("milestoneBulk.placeholders.status")}
                optionRender={renderMilestoneStatusOption}
              />
            </Form.Item>
          ) : null}

          {activeAction === "delete" ? (
            <Alert
              type="error"
              showIcon
              message={t("milestoneBulk.deleteImpact", {
                count: selectedIds.length
              })}
              description={t("milestoneBulk.deleteExtraWarning")}
            />
          ) : null}
        </Form>
      </Modal>
    </div>
  )
}
